import { Adp } from "@adsk/adp-web-analytics-sdk";
import { AuthClient } from "@adsk/auth-pkce-sdk";
import styled from "@emotion/styled";
import AutodeskAssistantIcon from "../images/icon-autodesk-assistant.svg";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { autodeskAssistantClientId, environment } from "./app.config";
import {
  PKCE_UNAUTHENTICATED_REDIRECT_PATH,
  QUALTRICS_FEEDBACK_CLOSED_EVENT,
  QUALTRICS_FEEDBACK_OPEN_EVENT,
} from "@app/constants.ts";
import { useAuth } from "@app/providers/AuthProvider.tsx";

const AssistantButton = styled.button({
  position: "fixed",
  bottom: "62px",
  right: "62px",
  zIndex: 999,
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: 0,
});

const AssistantIconWrapper = styled.div({
  boxSizing: "border-box",
  width: 50,
  height: 50,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px solid #000",
  borderRadius: "35px 35px 0 35px",
  background: "#fff",
});

type AssistantRenderedOn = "frontend-app-authenticated" | "drupal";

type AssistantAppState = {
  returnTo: string;
  renderedOn: AssistantRenderedOn;
};

/** Exported for unit tests; validates OAuth callback app_state payloads. */
export function parseAssistantAppState(
  value: unknown
): AssistantAppState | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const { returnTo, renderedOn } = value as AssistantAppState;
  if (typeof returnTo !== "string") {
    return null;
  }
  // Only same-origin relative paths are valid navigation targets.
  if (!returnTo.startsWith("/") || returnTo.startsWith("//")) {
    return null;
  }
  if (renderedOn !== "frontend-app-authenticated" && renderedOn !== "drupal") {
    return null;
  }

  return { returnTo, renderedOn };
}

function readAppStateFromStorage(): AssistantAppState | null {
  const appStateStr = localStorage.getItem("app_state");
  if (!appStateStr) {
    return null;
  }

  try {
    return parseAssistantAppState(JSON.parse(appStateStr));
  } catch {
    return null;
  }
}

/** Marks a /user/login return trip that should reopen the assistant. */
const OPEN_ASSISTANT_PARAM = "open_assistant";

/**
 * Exported for unit tests; builds the Django OIDC login URL.
 *
 * The Autodesk Assistant's PKCE session is entirely separate from our Django session, so
 * clicking the icon while anonymous to the backend has to go through Django's own login
 * flow first. We hand `/user/login` a `next` URL and nothing else — Django sets and
 * validates the session cookie itself.
 *
 * Any fragment is dropped because `next` is resolved server-side and fragments are never
 * sent in an HTTP request.
 */
export function buildDjangoLoginUrl(returnTo: string): string {
  // Same open-redirect guard as parseAssistantAppState.
  const safeReturnTo =
    returnTo.startsWith("/") && !returnTo.startsWith("//") ? returnTo : "/";
  const [pathAndSearch] = safeReturnTo.split("#");
  const [path, query] = pathAndSearch.split("?");

  const nextParams = new URLSearchParams(query);
  nextParams.set(OPEN_ASSISTANT_PARAM, "1");

  return `/user/login?next=${encodeURIComponent(`${path}?${nextParams}`)}`;
}

type AutodeskAssistantProps = {
  renderedOn: AssistantRenderedOn;

  /** For injecting alternative implementations or mocks during testing. */
  authImplementation?: Pick<
    AuthClient,
    | "isAuthenticated"
    | "getAccessTokenSilently"
    | "loginWithRedirect"
    | "handleRedirectCallback"
  > | null;
  adpImplementation?: {
    setAuthProvider: (...args: any[]) => any;
    loadUserConsentPreferences: (...args: any[]) => Promise<any>;
    showUserConsentDialog: () => Promise<void>;
    showAssistant: (options?: any) => Promise<void>;
    closeAssistant: () => void;
  } | null;
  /**
   * Full-page navigation, injectable because a real `window.location.assign` would
   * navigate the Cypress component-test iframe.
   */
  redirectImplementation?: ((url: string) => void) | null;
};

export function AutodeskAssistant({
  renderedOn,
  authImplementation = null,
  adpImplementation = null,
  redirectImplementation = null,
}: AutodeskAssistantProps) {
  const [_consentGiven, setConsentGiven] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isQualtricsFeedbackOpen, setIsQualtricsFeedbackOpen] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { pathname, search, hash, state: locationState } = useLocation();
  const { variation, authStatus, flagsStatus } = useAuth();

  const redirectTo =
    redirectImplementation ??
    ((url: string) => {
      window.location.assign(url);
    });

  // Feature-flag the ADP `build_id` so the Usage MCP config is only sent to the
  // Autodesk Assistant once `enable_usage_mcp` is turned on. Defaults to the
  // original config when the flag is off (or LaunchDarkly is unavailable).
  const buildId = variation("enable_usage_mcp", false)
    ? "USAGE.MCP.ENABLED"
    : "APS.ORIGINAL.CONFIG";

  // Scopes needed for User Profile service
  const userProfileScopes =
    "data:read user:read user:write openid data:create data:search profapi:core-all-profile:read profapi:img-profile:read profapi:core-all-profile:write profapi:product-prefs:read profapi:product-prefs:write";

  // Non-null assertions are safe here: this component is only rendered when
  // isAutodeskAssistantEnabled is true (i.e., commercial Dev/Stg/Prd environments).
  const auth = useMemo(
    () =>
      authImplementation ??
      new AuthClient({
        cacheLocation: "sessionstorage",
        client_id: autodeskAssistantClientId!,
        env: environment!,
        useRefreshTokens: true,
        // Must redirect to a path that loads the assistant.
        redirect_uri: `${window.location.origin}${PKCE_UNAUTHENTICATED_REDIRECT_PATH}`,
        scope: userProfileScopes,
      }),
    [authImplementation]
  );

  const adp = useMemo(
    () =>
      adpImplementation ??
      Adp().setEnv(environment!).load({
        name: "APSDEVPORTAL",
        id_provider: "appkey",
        id: autodeskAssistantClientId!,
        line_name: "APSDEVPORTAL",
        build_id: buildId,
      }),
    [adpImplementation, buildId]
  );

  // Wrapped in `useCallback` so the function can safely be passed as a
  // dependency to `useEffect`.
  const loadAssistantWithConsent = useCallback(async () => {
    // Register a live token callback so the SDK fetches a fresh token on every
    // orchestrator request rather than reusing a potentially stale static one.
    // Called here (on click) rather than on mount.
    adp.setAuthProvider({
      type: "generic",
      provider: {
        getAccessToken: (): Promise<string> => auth.getAccessTokenSilently(),
      },
    });

    const userToken = await auth.getAccessTokenSilently();
    const result = await adp.loadUserConsentPreferences(userToken);

    // The ADP SDK reports consent failures by *returning* an Error rather than
    // rejecting, so without this branch a failed consent fetch (e.g. the consent
    // service rejecting the Oxygen bearer token with a 401) leaves the assistant
    // silently never opening and nothing in the console to explain why.
    if (result instanceof Error) {
      console.error(
        "[AutodeskAssistant] Could not load user consent preferences; the assistant will not open:",
        result
      );
      return;
    }

    if (result.status.code === result.status.PENDING_USER_ACTION) {
      await adp.showUserConsentDialog();
    }
    setConsentGiven(true);

    await adp.showAssistant({
      theme: { color: "light-gray", density: "high" },
      margins: "62px 62px 0 0",
      urlSearchParameters: { icon: "pinwheel" },
    });
  }, [auth, adp]);

  const [isAssistantLoadPending, setIsAssistantLoadPending] = useState(false);

  // `build_id` comes from LaunchDarkly, so wait until the flags reflect the signed-in user.
  useEffect(
    function loadAssistantOnceFlagsSettle() {
      if (!isAssistantLoadPending) return;
      if (flagsStatus !== "loaded" && flagsStatus !== "error") return;

      setIsAssistantLoadPending(false);
      void loadAssistantWithConsent();
    },
    [isAssistantLoadPending, flagsStatus, loadAssistantWithConsent]
  );

  /**
   * The PKCE half of the click flow: reached directly when a Django session already
   * exists, or from `resumeAfterDjangoLogin` once one has been established. It never
   * redirects to /user/login, which is what makes a redirect loop impossible.
   */
  const startPkceLogin = useCallback(
    async (returnTo: string) => {
      if (!(await auth.isAuthenticated())) {
        const appState: AssistantAppState = { returnTo, renderedOn };
        await auth.loginWithRedirect({ appState: appState });
        return;
      }

      // Already authenticated, load assistant directly
      setIsAssistantLoadPending(true);
    },
    [auth, renderedOn]
  );

  const startPkceLoginRef = useRef(startPkceLogin);
  startPkceLoginRef.current = startPkceLogin;

  const resumeAfterAuth = useCallback(
    async (appState: AssistantAppState): Promise<boolean> => {
      const { returnTo, renderedOn } = appState;

      // Hand the open to the destination page: this redirect page unmounts on navigation.
      if (renderedOn === "frontend-app-authenticated") {
        navigate(returnTo, {
          replace: true,
          state: { openAutodeskAssistant: true },
        });
        return false;
      }

      window.history.replaceState(
        { openAutodeskAssistant: true },
        "",
        returnTo
      );
      location.reload();
      return true;
    },
    [navigate]
  );

  const codeSearchParam = searchParams.get("code");
  const redirectCallbackHandledRef = useRef(false);
  useEffect(
    function handleRedirectCallback() {
      const isAuthCallbackPath =
        pathname === PKCE_UNAUTHENTICATED_REDIRECT_PATH;

      if (!isAuthCallbackPath || !codeSearchParam) {
        return;
      }

      // OAuth authorization codes are single-use. Guard against effect re-runs
      // (e.g. when LaunchDarkly flags load and recreate `adp`) exchanging twice.
      if (redirectCallbackHandledRef.current) {
        return;
      }
      redirectCallbackHandledRef.current = true;

      (async () => {
        let navigatingAway = false;

        setIsAuthenticating(true);
        try {
          let parsedAppState: AssistantAppState | null = null;

          // Tokens may already be stored if a prior exchange succeeded but
          // navigation back to the original page did not complete — the same
          // state users reach by manually leaving the callback page.
          if (await auth.isAuthenticated()) {
            parsedAppState = readAppStateFromStorage();
          } else {
            const { appState } = await auth.handleRedirectCallback();
            parsedAppState = parseAssistantAppState(appState);
          }

          if (!parsedAppState) {
            // Can't use navigate() because it will only trigger client-side navigation.
            navigatingAway = true;
            window.location.href = "/";
            return;
          }

          navigatingAway = await resumeAfterAuth(parsedAppState);
        } catch (error) {
          console.error("[AutodeskAssistant] Callback failed:", error);

          // Recover when a concurrent exchange attempt already stored tokens.
          if (await auth.isAuthenticated()) {
            const fallbackAppState = readAppStateFromStorage();
            if (fallbackAppState) {
              navigatingAway = await resumeAfterAuth(fallbackAppState);
              return;
            }
          }

          navigatingAway = true;
          window.location.href = "/";
        } finally {
          if (!navigatingAway) {
            setIsAuthenticating(false);
          }
        }
      })();
    },
    [auth, pathname, codeSearchParam, resumeAfterAuth]
  );

  useEffect(
    function syncAssistantWithQualtricsFeedbackState() {
      function handleQualtricsFeedbackOpen() {
        setIsQualtricsFeedbackOpen(true);
        adp.closeAssistant();
      }

      function handleQualtricsFeedbackClosed() {
        setIsQualtricsFeedbackOpen(false);
      }

      window.addEventListener(
        QUALTRICS_FEEDBACK_OPEN_EVENT,
        handleQualtricsFeedbackOpen
      );
      window.addEventListener(
        QUALTRICS_FEEDBACK_CLOSED_EVENT,
        handleQualtricsFeedbackClosed
      );

      return function cleanup() {
        window.removeEventListener(
          QUALTRICS_FEEDBACK_OPEN_EVENT,
          handleQualtricsFeedbackOpen
        );
        window.removeEventListener(
          QUALTRICS_FEEDBACK_CLOSED_EVENT,
          handleQualtricsFeedbackClosed
        );
      };
    },
    [adp]
  );

  useEffect(
    function openAssistantOnArrival() {
      let cancel = false;

      (async () => {
        const isOpenRequested =
          history.state?.openAutodeskAssistant === true || // Drupal: replaceState + reload
          locationState?.openAutodeskAssistant === true; // Frontend app: navigate state
        if (!isOpenRequested) return;
        if (!(await auth.isAuthenticated())) return;

        if (cancel) return;

        setIsAssistantLoadPending(true);
      })();

      return function cleanup() {
        cancel = true;
      };
    },
    [auth, locationState]
  );

  const djangoResumeHandledRef = useRef(false);
  useEffect(
    function resumeAfterDjangoLogin() {
      if (searchParams.get(OPEN_ASSISTANT_PARAM) !== "1") {
        return;
      }

      // Wait for AuthProvider's /user/status; the effect re-runs once it settles.
      if (authStatus === "idle" || authStatus === "loading") {
        return;
      }

      // Effect re-runs (e.g. when LaunchDarkly flags load and recreate `adp`) must not
      // start a second login.
      if (djangoResumeHandledRef.current) {
        return;
      }
      djangoResumeHandledRef.current = true;

      // Strip the marker before computing returnTo, so it is not baked into the PKCE
      // app state and does not reopen the assistant on a later visit to this URL.
      const remainingParams = new URLSearchParams(searchParams);
      remainingParams.delete(OPEN_ASSISTANT_PARAM);
      const remainingQuery = remainingParams.toString();
      const returnTo = remainingQuery
        ? `${pathname}?${remainingQuery}`
        : pathname;
      navigate(returnTo, { replace: true });

      if (authStatus !== "authenticated") {
        // Django login was cancelled or failed. The marker is gone and only a click can
        // start another /user/login redirect, so stop here rather than retrying.
        console.warn(
          "[AutodeskAssistant] Django login did not complete; not resuming."
        );
        return;
      }

      // The PKCE client is still unauthenticated at this point, so re-enter the PKCE
      // login rather than loading the assistant directly.
      void startPkceLoginRef.current(returnTo);
    },
    [searchParams, authStatus, pathname, navigate]
  );

  async function authenticateFrontend() {
    // Django session first, blocking: the assistant must never load for a user who is
    // anonymous to our backend, because the PKCE session is entirely separate. Treating
    // "idle"/"loading"/"error" as unauthenticated is deliberate — a needless login hop is
    // silent for a user who already has an Oxygen SSO session, whereas letting an
    // anonymous user through is the bug this guards against.
    if (authStatus !== "authenticated") {
      // Hides the icon so a second click cannot race the navigation.
      setIsAuthenticating(true);
      redirectTo(buildDjangoLoginUrl(pathname + search + hash));
      return;
    }

    await startPkceLogin(pathname + search + hash);
  }

  if (isAuthenticating || isQualtricsFeedbackOpen) {
    return null;
  }

  return (
    <AssistantButton
      onClick={() => void authenticateFrontend()}
      aria-label="Open Autodesk Assistant"
      // Stable hook for the Autodesk Assistant Web E2E canary in ADP/assistant-ui;
      // renaming it breaks that external test.
      data-testid="aps-assistant-button"
    >
      <AssistantIconWrapper>
        <AutodeskAssistantIcon
          width={32}
          height={32}
          aria-hidden="true"
          focusable="false"
        />
      </AssistantIconWrapper>
    </AssistantButton>
  );
}
